import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { banksApi } from '../services/api';
import type { BankOption, BankSummary } from '../types';

/** The active banks, loaded once when `enabled` (only a Privatperson needs them). */
export function useBankOptions(enabled = true) {
  const [banks, setBanks] = useState<BankOption[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    banksApi
      .list()
      .then(list => { if (active) setBanks(Array.isArray(list) ? list : []); })
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [enabled]);

  return { banks, loadError };
}

type BankPickerProps = {
  /** The active banks to choose among. */
  banks: BankOption[];
  value: string[];
  onChange: (bankIds: string[]) => void;
  /** Banks already on the profile that Admin no longer offers: kept, shown, not added. */
  current?: BankSummary[];
  disabled?: boolean;
  /** Shows "Välj minst en bank." once the person has tried to go on. */
  showRequired?: boolean;
};

/**
 * A Privatperson's banks: at least one of the supported banks (ClickUp
 * 869eu6fpm). Below the list: more banks may come, and Swish works for
 * smaller amounts (ClickUp 869fmtxvv).
 */
export function BankPicker({ banks, value, onChange, current = [], disabled, showRequired }: BankPickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const chosen = new Set(value);
  const retired = current.filter(bank => !bank.isActive && chosen.has(bank.id));
  const options: BankOption[] = [...banks, ...retired];
  const missing = showRequired && value.length === 0;

  const toggle = (bankId: string) => {
    onChange(chosen.has(bankId) ? value.filter(idValue => idValue !== bankId) : [...value, bankId]);
  };

  return (
    <fieldset className="bank-picker mb-3" aria-describedby={`${id}-help`} data-testid="bank-picker">
      <legend className="form-label fw-semibold mb-1">
        {t('banks.label')} <span className="text-danger" aria-hidden="true">*</span>
      </legend>
      <p id={`${id}-help`} className="form-text mt-0 mb-2">{t('banks.help')}</p>
      <div className="bank-picker-options">
        {options.map(bank => (
          <label key={bank.id} className="bank-picker-option form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={chosen.has(bank.id)}
              disabled={disabled}
              onChange={() => toggle(bank.id)}
              data-testid={`bank-option-${bank.name}`}
            />
            <span className="form-check-label">
              {'isActive' in bank && bank.isActive === false
                ? t('banks.inactive', { name: bank.name })
                : bank.name}
            </span>
          </label>
        ))}
      </div>
      {missing && <div className="invalid-feedback d-block" role="alert">{t('banks.chooseOne')}</div>}
      <div className="alert alert-info small mt-2 mb-0" data-testid="bank-info">
        <p className="mb-1">{t('banks.supportedInfo')}</p>
        <p className="mb-0">{t('banks.swishInfo')}</p>
      </div>
    </fieldset>
  );
}
